import { useState } from 'react';
import { CampanhaSimulada, MetaGerada, DistintivoGerado, MissaoGerada } from '../../types/simulador';

interface EditableSimulacaoPreviewProps {
  campanha: CampanhaSimulada;
  onCampanhaChange: (campanha: CampanhaSimulada) => void;
  campanhaBaseId?: number | null; // NOVO: ID da campanha base para identificar elementos existentes
}

export function EditableSimulacaoPreview({ campanha, onCampanhaChange, campanhaBaseId }: EditableSimulacaoPreviewProps) {
  const [editandoMeta, setEditandoMeta] = useState<string | null>(null);
  const [editandoDistintivo, setEditandoDistintivo] = useState<string | null>(null);
  const [editandoMissao, setEditandoMissao] = useState<string | null>(null);
  const [editandoRecompensa, setEditandoRecompensa] = useState<string | null>(null);
  const [alertas, setAlertas] = useState<string[]>([]);
  const [abaAtiva, setAbaAtiva] = useState<'resumo' | 'metas' | 'distintivos' | 'progressao' | 'regras'>('resumo');

  // NOVO: Função para verificar se um elemento é da campanha base ou gerado pela IA
  const isElementoExistente = (elementoId: string): boolean => {
    if (!campanhaBaseId) return false;
    // IDs que começam com 'm', 'd', 'mis' seguidos de número são da campanha base
    // IDs que começam com 'meta-', 'dist-', 'missao-' seguidos de timestamp são gerados pela IA
    return /^(m|d|mis)\d+$/.test(elementoId);
  };

  const adicionarAlerta = (mensagem: string) => {
    setAlertas(prev => [...prev, mensagem]);
    setTimeout(() => {
      setAlertas(prev => prev.filter(a => a !== mensagem));
    }, 5000);
  };

  const atualizarMeta = (metaId: string, campo: keyof MetaGerada, valor: any) => {
    const novasMetas = campanha.metas.map(m => 
      m.id === metaId ? { ...m, [campo]: valor } : m
    );
    
    const metaAtualizada = novasMetas.find(m => m.id === metaId);
    if (metaAtualizada) {
      adicionarAlerta(`⚠️ Meta "${metaAtualizada.descricao}" foi alterada. Revise os custos!`);
    }
    
    onCampanhaChange({ ...campanha, metas: novasMetas });
  };

  const removerMeta = (metaId: string) => {
    if (confirm('Deseja realmente remover esta meta?')) {
      const novasMetas = campanha.metas.filter(m => m.id !== metaId);
      adicionarAlerta('⚠️ Meta removida. O custo total foi recalculado.');
      onCampanhaChange({ ...campanha, metas: novasMetas });
    }
  };

  const adicionarMeta = () => {
    const novaMeta: MetaGerada = {
      id: `meta-${Date.now()}`,
      descricao: 'Nova Meta',
      tipo: 'volume',
      valorMeta: 10,
      pontos: 500,
      nivel: 'Bronze'
    };
    
    adicionarAlerta('✅ Nova meta adicionada. Configure os valores!');
    onCampanhaChange({ ...campanha, metas: [...campanha.metas, novaMeta] });
    setEditandoMeta(novaMeta.id);
  };

  const atualizarDistintivo = (distintivoId: string, campo: keyof DistintivoGerado, valor: any) => {
    const novosDistintivos = campanha.distintivos.map(d => 
      d.id === distintivoId ? { ...d, [campo]: valor } : d
    );
    
    const distintivoAtualizado = novosDistintivos.find(d => d.id === distintivoId);
    if (distintivoAtualizado) {
      adicionarAlerta(`⚠️ Distintivo "${distintivoAtualizado.nome}" foi alterado.`);
    }
    
    onCampanhaChange({ ...campanha, distintivos: novosDistintivos });
  };

  const removerDistintivo = (distintivoId: string) => {
    if (confirm('Deseja realmente remover este distintivo?')) {
      const novosDistintivos = campanha.distintivos.filter(d => d.id !== distintivoId);
      adicionarAlerta('⚠️ Distintivo removido.');
      onCampanhaChange({ ...campanha, distintivos: novosDistintivos });
    }
  };

  const adicionarDistintivo = () => {
    const novoDistintivo: DistintivoGerado = {
      id: `dist-${Date.now()}`,
      nome: 'Novo Distintivo',
      descricao: 'Descrição do distintivo',
      icone: 'ri-medal-fill',
      condicao: 'Condição para conquistar',
      xp: 500,
      cor: 'blue'
    };
    
    adicionarAlerta('✅ Novo distintivo adicionado. Configure os valores!');
    onCampanhaChange({ ...campanha, distintivos: [...campanha.distintivos, novoDistintivo] });
    setEditandoDistintivo(novoDistintivo.id);
  };

  const atualizarMissao = (missaoId: string, campo: keyof MissaoGerada, valor: any) => {
    if (!campanha.progressao) return;
    
    const novasMissoes = campanha.progressao.missoes.map(m => 
      m.id === missaoId ? { ...m, [campo]: valor } : m
    );
    
    const missaoAtualizada = novasMissoes.find(m => m.id === missaoId);
    if (missaoAtualizada) {
      adicionarAlerta(`⚠️ Missão "${missaoAtualizada.titulo}" foi alterada.`);
    }
    
    onCampanhaChange({ 
      ...campanha, 
      progressao: { ...campanha.progressao, missoes: novasMissoes }
    });
  };

  const removerMissao = (missaoId: string) => {
    if (!campanha.progressao) return;
    
    if (confirm('Deseja realmente remover esta missão?')) {
      const novasMissoes = campanha.progressao.missoes.filter(m => m.id !== missaoId);
      adicionarAlerta('⚠️ Missão removida.');
      onCampanhaChange({ 
        ...campanha, 
        progressao: { ...campanha.progressao, missoes: novasMissoes }
      });
    }
  };

  const adicionarMissao = () => {
    if (!campanha.progressao) return;
    
    const novaMissao: MissaoGerada = {
      id: `missao-${Date.now()}`,
      titulo: 'Nova Missão',
      descricao: 'Descrição da missão',
      tipo: 'diaria',
      xp: 50,
      nivel: 'Bronze'
    };
    
    adicionarAlerta('✅ Nova missão adicionada. Configure os valores!');
    onCampanhaChange({ 
      ...campanha, 
      progressao: { ...campanha.progressao, missoes: [...campanha.progressao.missoes, novaMissao] }
    });
    setEditandoMissao(novaMissao.id);
  };

  // Calcular métricas gerenciais
  const totalMetas = campanha.metas.length;
  const totalDistintivos = campanha.distintivos.length;
  const totalMissoes = campanha.progressao?.missoes.length || 0;
  const totalNiveis = campanha.progressao?.niveis.length || 0;
  const totalParticipantes = campanha.participantes.length;
  const totalProdutos = campanha.produtosServicos.length;

  // NOVO: Separar elementos existentes e gerados pela IA
  const metasExistentes = campanha.metas.filter(m => isElementoExistente(m.id));
  const metasGeradas = campanha.metas.filter(m => !isElementoExistente(m.id));
  
  const distintivosExistentes = campanha.distintivos.filter(d => isElementoExistente(d.id));
  const distintivosGerados = campanha.distintivos.filter(d => !isElementoExistente(d.id));
  
  const missoesExistentes = campanha.progressao?.missoes.filter(m => isElementoExistente(m.id)) || [];
  const missoesGeradas = campanha.progressao?.missoes.filter(m => !isElementoExistente(m.id)) || [];

  const pontosMaximos = campanha.metas.reduce((sum, meta) => sum + meta.pontos, 0);
  const xpMaximo = campanha.distintivos.reduce((sum, dist) => sum + dist.xp, 0) + 
                   (campanha.progressao?.missoes.reduce((sum, mis) => sum + mis.xp, 0) || 0);

  const metasPorNivel = {
    Bronze: campanha.metas.filter(m => m.nivel === 'Bronze').length,
    Prata: campanha.metas.filter(m => m.nivel === 'Prata').length,
    Ouro: campanha.metas.filter(m => m.nivel === 'Ouro').length,
    Platina: campanha.metas.filter(m => m.nivel === 'Platina').length,
    Diamante: campanha.metas.filter(m => m.nivel === 'Diamante').length,
  };

  // NOVO: Componente de Badge para identificar origem
  const BadgeOrigem = ({ isExistente }: { isExistente: boolean }) => (
    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
      isExistente 
        ? 'bg-blue-100 text-blue-800 border border-blue-300' 
        : 'bg-green-100 text-green-800 border border-green-300'
    }`}>
      {isExistente ? '📋 Existente' : '✨ IA'}
    </span>
  );

  // Dados reais dos níveis do sistema (mesmos de /admin/gamificacao/niveis)
  const NIVEIS_SISTEMA = [
    {
      id: 1,
      nivel: 'Bronze',
      xpNecessario: 0,
      xpProximo: 1000,
      cor: '#CD7F32',
      icone: '🥉'
    },
    {
      id: 2,
      nivel: 'Prata',
      xpNecessario: 1000,
      xpProximo: 5000,
      cor: '#C0C0C0',
      icone: '🥈'
    },
    {
      id: 3,
      nivel: 'Ouro',
      xpNecessario: 5000,
      xpProximo: 15000,
      cor: '#FFD700',
      icone: '🥇'
    },
    {
      id: 4,
      nivel: 'Platina',
      xpNecessario: 15000,
      xpProximo: 50000,
      cor: '#E5E4E2',
      icone: '💎'
    },
    {
      id: 5,
      nivel: 'Diamante',
      xpNecessario: 50000,
      xpProximo: 999999,
      cor: '#B9F2FF',
      icone: '💠'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Alertas de Edição */}
      {alertas.length > 0 && (
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {alertas.map((alerta, index) => (
            <div key={index} className="bg-orange-500 text-white px-4 py-3 rounded-lg shadow-lg animate-slide-in-right">
              {alerta}
            </div>
          ))}
        </div>
      )}

      {/* Header Executivo */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-2xl font-bold mb-2">{campanha.nome}</h3>
            <p className="text-orange-100 mb-4">{campanha.descricao}</p>
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                <span>{campanha.dataInicio} até {campanha.dataFim}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-group-line"></i>
                <span>{totalParticipantes} participantes</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-shopping-bag-line"></i>
                <span>{totalProdutos} produtos/serviços</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg backdrop-blur-sm">
            <i className="ri-bar-chart-box-line text-xl"></i>
            <span className="font-semibold">Visão Gerencial</span>
          </div>
        </div>
      </div>

      {/* NOVO: Resumo de Origem dos Elementos */}
      {campanhaBaseId && (
        <div className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200 rounded-lg p-5">
          <div className="flex items-start gap-3 mb-4">
            <i className="ri-information-line text-blue-600 text-2xl mt-0.5"></i>
            <div className="flex-1">
              <h4 className="text-base font-bold text-gray-900 mb-2">📊 Análise de Origem dos Elementos</h4>
              <p className="text-sm text-gray-700 mb-3">
                Esta campanha combina elementos existentes com sugestões geradas pela IA
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h5 className="text-sm font-semibold text-gray-900">Metas</h5>
                <i className="ri-target-line text-green-600 text-xl"></i>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">📋 Existentes:</span>
                  <span className="text-lg font-bold text-blue-600">{metasExistentes.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">✨ Geradas IA:</span>
                  <span className="text-lg font-bold text-green-600">{metasGeradas.length}</span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-900">Total:</span>
                    <span className="text-xl font-bold text-orange-600">{totalMetas}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h5 className="text-sm font-semibold text-gray-900">Distintivos</h5>
                <i className="ri-medal-line text-purple-600 text-xl"></i>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">📋 Existentes:</span>
                  <span className="text-lg font-bold text-blue-600">{distintivosExistentes.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">✨ Gerados IA:</span>
                  <span className="text-lg font-bold text-green-600">{distintivosGerados.length}</span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-900">Total:</span>
                    <span className="text-xl font-bold text-orange-600">{totalDistintivos}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h5 className="text-sm font-semibold text-gray-900">Missões</h5>
                <i className="ri-trophy-line text-blue-600 text-xl"></i>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">📋 Existentes:</span>
                  <span className="text-lg font-bold text-blue-600">{missoesExistentes.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">✨ Geradas IA:</span>
                  <span className="text-lg font-bold text-green-600">{missoesGeradas.length}</span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-900">Total:</span>
                    <span className="text-xl font-bold text-orange-600">{totalMissoes}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPIs Principais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-target-line text-green-600 text-xl"></i>
            </div>
            <span className="text-2xl font-bold text-gray-900">{totalMetas}</span>
          </div>
          <p className="text-sm text-gray-600 font-medium">Metas Configuradas</p>
          <p className="text-xs text-gray-500 mt-1">{pontosMaximos.toLocaleString('pt-BR')} pontos totais</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-medal-line text-purple-600 text-xl"></i>
            </div>
            <span className="text-2xl font-bold text-gray-900">{totalDistintivos}</span>
          </div>
          <p className="text-sm text-gray-600 font-medium">Distintivos</p>
          <p className="text-xs text-gray-500 mt-1">{xpMaximo.toLocaleString('pt-BR')} XP total</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-trophy-line text-blue-600 text-xl"></i>
            </div>
            <span className="text-2xl font-bold text-gray-900">{totalMissoes}</span>
          </div>
          <p className="text-sm text-gray-600 font-medium">Missões</p>
          <p className="text-xs text-gray-500 mt-1">{totalNiveis} níveis de progressão</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <i className="ri-line-chart-line text-orange-600 text-xl"></i>
            </div>
            <span className="text-2xl font-bold text-gray-900">{campanha.analiseComplexidade?.nivel || 'N/A'}</span>
          </div>
          <p className="text-sm text-gray-600 font-medium">Complexidade</p>
          <p className="text-xs text-gray-500 mt-1">Nível de gestão</p>
        </div>
      </div>

      {/* Navegação por Abas */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex">
            <button
              onClick={() => setAbaAtiva('resumo')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                abaAtiva === 'resumo'
                  ? 'bg-white text-orange-600 border-b-2 border-orange-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <i className="ri-dashboard-line mr-2"></i>
              Resumo Executivo
            </button>
            <button
              onClick={() => setAbaAtiva('regras')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                abaAtiva === 'regras'
                  ? 'bg-white text-orange-600 border-b-2 border-orange-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <i className="ri-settings-3-line mr-2"></i>
              Regras & Análise
            </button>
            <button
              onClick={() => setAbaAtiva('metas')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                abaAtiva === 'metas'
                  ? 'bg-white text-orange-600 border-b-2 border-orange-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <i className="ri-target-line mr-2"></i>
              Metas ({totalMetas})
            </button>
            <button
              onClick={() => setAbaAtiva('distintivos')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                abaAtiva === 'distintivos'
                  ? 'bg-white text-orange-600 border-b-2 border-orange-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <i className="ri-medal-line mr-2"></i>
              Distintivos ({totalDistintivos})
            </button>
            <button
              onClick={() => setAbaAtiva('progressao')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                abaAtiva === 'progressao'
                  ? 'bg-white text-orange-600 border-b-2 border-orange-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <i className="ri-bar-chart-box-line mr-2"></i>
              Progressão ({totalMissoes})
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* ABA: Resumo Executivo */}
          {abaAtiva === 'resumo' && (
            <div className="space-y-6">
              {/* Distribuição de Metas por Nível */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-pie-chart-line text-orange-600"></i>
                  Distribuição de Metas por Nível
                </h4>
                <div className="grid grid-cols-5 gap-3">
                  {Object.entries(metasPorNivel).map(([nivel, quantidade]) => {
                    const cores = {
                      Bronze: 'bg-amber-100 text-amber-800 border-amber-300',
                      Prata: 'bg-gray-100 text-gray-800 border-gray-300',
                      Ouro: 'bg-yellow-100 text-yellow-800 border-yellow-300',
                      Platina: 'bg-cyan-100 text-cyan-800 border-cyan-300',
                      Diamante: 'bg-blue-100 text-blue-800 border-blue-300',
                    };
                    const porcentagem = totalMetas > 0 ? ((quantidade / totalMetas) * 100).toFixed(0) : 0;
                    
                    return (
                      <div key={nivel} className={`border-2 rounded-lg p-4 ${cores[nivel as keyof typeof cores]}`}>
                        <div className="text-center">
                          <div className="text-3xl font-bold mb-1">{quantidade}</div>
                          <div className="text-xs font-medium mb-2">{nivel}</div>
                          <div className="text-xs opacity-75">{porcentagem}%</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Análise de Recompensas */}
              <div className="grid grid-cols-2 gap-6">
                <div className="border border-gray-200 rounded-lg p-5">
                  <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-coin-line text-green-600"></i>
                    Sistema de Pontos
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total de Pontos Disponíveis</span>
                      <span className="text-lg font-bold text-green-600">{pontosMaximos.toLocaleString('pt-BR')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Média por Meta</span>
                      <span className="text-base font-semibold text-gray-900">
                        {totalMetas > 0 ? Math.round(pontosMaximos / totalMetas).toLocaleString('pt-BR') : 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Metas Configuradas</span>
                      <span className="text-base font-semibold text-gray-900">{totalMetas}</span>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-5">
                  <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-star-line text-purple-600"></i>
                    Sistema de XP
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total de XP Disponível</span>
                      <span className="text-lg font-bold text-purple-600">{xpMaximo.toLocaleString('pt-BR')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Distintivos</span>
                      <span className="text-base font-semibold text-gray-900">{totalDistintivos}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Missões</span>
                      <span className="text-base font-semibold text-gray-900">{totalMissoes}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Produtos e Participantes */}
              <div className="grid grid-cols-2 gap-6">
                <div className="border border-gray-200 rounded-lg p-5">
                  <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-shopping-bag-line text-blue-600"></i>
                    Produtos/Serviços ({totalProdutos})
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {campanha.produtosServicos.map((produto) => (
                      <div key={produto.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{produto.descricao}</p>
                          <p className="text-xs text-gray-500">{produto.codigo}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            produto.tipo === 'produto' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {produto.tipo}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-5">
                  <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-group-line text-orange-600"></i>
                    Participantes ({totalParticipantes})
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {campanha.participantes.slice(0, 10).map((participante) => (
                      <div key={participante.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{participante.nome}</p>
                          <p className="text-xs text-gray-500">{participante.departamento} - {participante.cargo}</p>
                        </div>
                      </div>
                    ))}
                    {totalParticipantes > 10 && (
                      <div className="text-center py-2 text-sm text-gray-500">
                        + {totalParticipantes - 10} participantes
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Indicadores de Engajamento */}
              <div className="border border-gray-200 rounded-lg p-5 bg-gradient-to-br from-blue-50 to-purple-50">
                <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-pulse-line text-blue-600"></i>
                  Indicadores de Engajamento Previstos
                </h4>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600 mb-1">{totalMetas + totalDistintivos + totalMissoes}</div>
                    <div className="text-xs text-gray-600">Oportunidades de Recompensa</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600 mb-1">{totalNiveis}</div>
                    <div className="text-xs text-gray-600">Níveis de Progressão</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600 mb-1">
                      {Math.round((totalMetas + totalDistintivos + totalMissoes) / totalParticipantes)}
                    </div>
                    <div className="text-xs text-gray-600">Recompensas por Participante</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600 mb-1">
                      {Math.round((pontosMaximos + xpMaximo) / totalParticipantes)}
                    </div>
                    <div className="text-xs text-gray-600">Pontos/XP Médio</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* NOVA ABA: Regras & Análise */}
          {abaAtiva === 'regras' && (
            <div className="space-y-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-settings-3-line text-orange-600"></i>
                Regras de Negócio e Análise Detalhada
              </h4>

              {/* Matriz Peso × Perfil */}
              <div className="border border-gray-200 rounded-lg p-5 bg-gradient-to-br from-blue-50 to-purple-50">
                <h5 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-grid-line text-blue-600"></i>
                  Matriz Peso Comercial × Perfil Econômico
                </h5>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <h6 className="text-sm font-semibold text-gray-900 mb-3">📊 Peso Comercial dos Produtos</h6>
                    <div className="space-y-2">
                      {campanha.produtosServicos.map((produto) => (
                        <div key={produto.id} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700 truncate flex-1">{produto.descricao}</span>
                          <span className={`px-2 py-1 rounded text-xs font-bold ml-2 ${
                            produto.pesoComercial === 'Estratégico' ? 'bg-red-100 text-red-800' :
                            produto.pesoComercial === 'Alto' ? 'bg-orange-100 text-orange-800' :
                            produto.pesoComercial === 'Médio' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {produto.pesoComercial}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-purple-200">
                    <h6 className="text-sm font-semibold text-gray-900 mb-3">💰 Perfil Econômico dos Produtos</h6>
                    <div className="space-y-2">
                      {campanha.produtosServicos.map((produto) => (
                        <div key={produto.id} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700 truncate flex-1">{produto.descricao}</span>
                          <span className={`px-2 py-1 rounded text-xs font-bold ml-2 ${
                            produto.perfilEconomico === 'Estratégico' ? 'bg-purple-100 text-purple-800' :
                            produto.perfilEconomico === 'Tolerante' ? 'bg-blue-100 text-blue-800' :
                            produto.perfilEconomico === 'Equilibrado' ? 'bg-cyan-100 text-cyan-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {produto.perfilEconomico}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <h6 className="text-sm font-semibold text-gray-900 mb-2">📝 Como a IA Utilizou a Matriz</h6>
                  <div className="space-y-1 text-sm text-gray-700">
                    <p>• <strong>Peso Comercial Alto/Estratégico</strong>: Metas com pontuação maior e níveis superiores (Ouro, Platina, Diamante)</p>
                    <p>• <strong>Perfil Econômico Tolerante/Estratégico</strong>: Recompensas mais generosas e distintivos especiais</p>
                    <p>• <strong>Combinação Estratégica</strong>: Produtos com ambos os atributos estratégicos recebem as melhores recompensas</p>
                    <p>• <strong>Balanceamento</strong>: Produtos com peso/perfil baixo recebem metas Bronze/Prata com pontuação proporcional</p>
                  </div>
                </div>
              </div>

              {/* Análise de Distribuição */}
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-5">
                  <h5 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-pie-chart-line text-green-600"></i>
                    Distribuição de Pontos por Nível
                  </h5>
                  <div className="space-y-3">
                    {Object.entries(metasPorNivel).map(([nivel, quantidade]) => {
                      const pontosPorNivel = campanha.metas
                        .filter(m => m.nivel === nivel)
                        .reduce((sum, m) => sum + m.pontos, 0);
                      const porcentagem = pontosMaximos > 0 ? ((pontosPorNivel / pontosMaximos) * 100).toFixed(1) : 0;
                      
                      return (
                        <div key={nivel} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-full ${
                              nivel === 'Bronze' ? 'bg-amber-500' :
                              nivel === 'Prata' ? 'bg-gray-400' :
                              nivel === 'Ouro' ? 'bg-yellow-500' :
                              nivel === 'Platina' ? 'bg-cyan-500' :
                              'bg-blue-500'
                            }`}></span>
                            <span className="text-sm text-gray-700">{nivel}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold text-gray-900">
                              {pontosPorNivel.toLocaleString('pt-BR')} pts
                            </span>
                            <span className="text-xs text-gray-500 w-12 text-right">{porcentagem}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-5">
                  <h5 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-bar-chart-line text-purple-600"></i>
                    Distribuição de XP
                  </h5>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <i className="ri-medal-line text-purple-600"></i>
                        <span className="text-sm text-gray-700">Distintivos</span>
                      </div>
                      <span className="text-sm font-semibold text-purple-600">
                        {campanha.distintivos.reduce((sum, d) => sum + d.xp, 0).toLocaleString('pt-BR')} XP
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <i className="ri-trophy-line text-blue-600"></i>
                        <span className="text-sm text-gray-700">Missões</span>
                      </div>
                      <span className="text-sm font-semibold text-blue-600">
                        {(campanha.progressao?.missoes.reduce((sum, m) => sum + m.xp, 0) || 0).toLocaleString('pt-BR')} XP
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border-2 border-orange-200">
                      <div className="flex items-center gap-2">
                        <i className="ri-star-line text-orange-600"></i>
                        <span className="text-sm font-bold text-gray-900">Total XP</span>
                      </div>
                      <span className="text-base font-bold text-orange-600">
                        {xpMaximo.toLocaleString('pt-BR')} XP
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Análise de Complexidade */}
              {campanha.analiseComplexidade && (
                <div className="border border-gray-200 rounded-lg p-5 bg-gradient-to-br from-orange-50 to-red-50">
                  <h5 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-line-chart-line text-orange-600"></i>
                    Análise de Complexidade da Campanha
                  </h5>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-4 text-center border border-gray-200">
                      <div className="text-3xl font-bold text-orange-600 mb-1">
                        {campanha.analiseComplexidade.nivel}
                      </div>
                      <div className="text-xs text-gray-600">Nível de Complexidade</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center border border-gray-200">
                      <div className="text-3xl font-bold text-blue-600 mb-1">
                        {campanha.analiseComplexidade.pontuacao}
                      </div>
                      <div className="text-xs text-gray-600">Pontuação</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center border border-gray-200">
                      <div className="text-3xl font-bold text-green-600 mb-1">
                        {campanha.analiseComplexidade.tempoGestaoEstimado}
                      </div>
                      <div className="text-xs text-gray-600">Tempo de Gestão</div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h6 className="text-sm font-semibold text-gray-900 mb-2">Recomendações:</h6>
                    <ul className="space-y-1 text-sm text-gray-700">
                      {campanha.analiseComplexidade?.recomendacoes?.map((rec, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-green-600 mt-0.5"></i>
                          <span>{rec}</span>
                        </li>
                      )) || (
                        <li className="text-gray-500 text-sm">Nenhuma recomendação disponível</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ABA: Metas */}
          {abaAtiva === 'metas' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">Todas as Metas ({totalMetas})</h4>
                <button
                  onClick={adicionarMeta}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2 text-sm whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  Adicionar Meta
                </button>
              </div>

              {campanha.metas.map((meta) => {
                const isExistente = isElementoExistente(meta.id);
                return (
                  <div key={meta.id} className={`border-2 rounded-lg p-4 hover:shadow-md transition-shadow ${
                    isExistente ? 'border-blue-200 bg-blue-50/30' : 'border-green-200 bg-green-50/30'
                  }`}>
                    {editandoMeta === meta.id ? (
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-gray-600 block mb-1">Descrição</label>
                          <input
                            type="text"
                            value={meta.descricao}
                            onChange={(e) => atualizarMeta(meta.id, 'descricao', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-xs text-gray-600 block mb-1">Tipo</label>
                            <select
                              value={meta.tipo}
                              onChange={(e) => atualizarMeta(meta.id, 'tipo', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="volume">Volume</option>
                              <option value="valor">Valor</option>
                              <option value="frequencia">Frequência</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 block mb-1">Valor Meta</label>
                            <input
                              type="number"
                              value={meta.valorMeta}
                              onChange={(e) => atualizarMeta(meta.id, 'valorMeta', Number(e.target.value))}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 block mb-1">Pontos</label>
                            <input
                              type="number"
                              value={meta.pontos}
                              onChange={(e) => atualizarMeta(meta.id, 'pontos', Number(e.target.value))}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 block mb-1">Nível</label>
                          <select
                            value={meta.nivel}
                            onChange={(e) => atualizarMeta(meta.id, 'nivel', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value="Bronze">Bronze</option>
                            <option value="Prata">Prata</option>
                            <option value="Ouro">Ouro</option>
                            <option value="Platina">Platina</option>
                            <option value="Diamante">Diamante</option>
                          </select>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditandoMeta(null)}
                            className="flex-1 px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 text-sm whitespace-nowrap"
                          >
                            Salvar
                          </button>
                          <button
                            onClick={() => removerMeta(meta.id)}
                            className="px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 text-sm whitespace-nowrap"
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <BadgeOrigem isExistente={isExistente} />
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              meta.nivel === 'Bronze' ? 'bg-amber-100 text-amber-800' :
                              meta.nivel === 'Prata' ? 'bg-gray-100 text-gray-800' :
                              meta.nivel === 'Ouro' ? 'bg-yellow-100 text-yellow-800' :
                              meta.nivel === 'Platina' ? 'bg-cyan-100 text-cyan-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {meta.nivel}
                            </span>
                            <span className="text-sm text-gray-600">
                              {meta.tipo === 'volume' ? 'Volume' : meta.tipo === 'valor' ? 'Valor' : 'Frequência'}
                            </span>
                          </div>
                          <h4 className="text-base font-semibold text-gray-900 mb-1">{meta.descricao}</h4>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <i className="ri-flag-line text-gray-500"></i>
                              <span>Meta: {meta.valorMeta.toLocaleString('pt-BR')}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <i className="ri-coin-line text-green-600"></i>
                              <span className="font-semibold text-green-600">{meta.pontos.toLocaleString('pt-BR')} pontos</span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => setEditandoMeta(meta.id)}
                          className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                        >
                          <i className="ri-edit-line text-lg"></i>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ABA: Distintivos */}
          {abaAtiva === 'distintivos' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">Todos os Distintivos ({totalDistintivos})</h4>
                <button
                  onClick={adicionarDistintivo}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center gap-2 text-sm whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  Adicionar Distintivo
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {campanha.distintivos.map((distintivo) => {
                  const isExistente = isElementoExistente(distintivo.id);
                  return (
                    <div key={distintivo.id} className={`border-2 rounded-lg p-4 hover:shadow-md transition-shadow ${
                      isExistente ? 'border-blue-200 bg-blue-50/30' : 'border-green-200 bg-green-50/30'
                    }`}>
                      {editandoDistintivo === distintivo.id ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs text-gray-600 block mb-1">Nome</label>
                              <input
                                type="text"
                                value={distintivo.nome}
                                onChange={(e) => atualizarDistintivo(distintivo.id, 'nome', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-600 block mb-1">XP</label>
                              <input
                                type="number"
                                value={distintivo.pontos}
                                onChange={(e) => atualizarDistintivo(distintivo.id, 'pontos', Number(e.target.value))}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 block mb-1">Descrição</label>
                            <input
                              type="text"
                              value={distintivo.descricao}
                              onChange={(e) => atualizarDistintivo(distintivo.id, 'descricao', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 block mb-1">Condição</label>
                            <input
                              type="text"
                              value={distintivo.condicao}
                              onChange={(e) => atualizarDistintivo(distintivo.id, 'condicao', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setEditandoDistintivo(null)}
                              className="flex-1 px-3 py-1.5 bg-purple-500 text-white rounded hover:bg-purple-600 text-sm whitespace-nowrap"
                            >
                              Salvar
                            </button>
                            <button
                              onClick={() => removerDistintivo(distintivo.id)}
                              className="px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 text-sm whitespace-nowrap"
                            >
                              Remover
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-3">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            distintivo.cor === 'blue' ? 'bg-blue-100' :
                            distintivo.cor === 'green' ? 'bg-green-100' :
                            distintivo.cor === 'purple' ? 'bg-purple-100' :
                            distintivo.cor === 'yellow' ? 'bg-yellow-100' :
                            'bg-orange-100'
                          }`}>
                            <i className={`${distintivo.icone} text-2xl ${
                              distintivo.cor === 'blue' ? 'text-blue-600' :
                              distintivo.cor === 'green' ? 'text-green-600' :
                              distintivo.cor === 'purple' ? 'text-purple-600' :
                              distintivo.cor === 'yellow' ? 'text-yellow-600' :
                              'text-orange-600'
                            }`}></i>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <BadgeOrigem isExistente={isExistente} />
                            </div>
                            <h4 className="text-base font-semibold text-gray-900 mb-1">{distintivo.nome}</h4>
                            <p className="text-sm text-gray-600 mb-2">{distintivo.descricao}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">{distintivo.condicao}</span>
                              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-semibold">
                                +{distintivo.xp} XP
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => setEditandoDistintivo(distintivo.id)}
                            className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                          >
                            <i className="ri-edit-line text-lg"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ABA: Progressão */}
          {abaAtiva === 'progressao' && campanha.progressao && (
            <div className="space-y-6">
              {/* Níveis de Progressão */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <i className="ri-bar-chart-box-line text-blue-600"></i>
                    Níveis de Progressão
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {NIVEIS_SISTEMA.map((nivel) => (
                    <div
                      key={nivel.id}
                      className="bg-white border-2 rounded-xl p-5 shadow-sm hover:shadow-md transition-all"
                      style={{ borderColor: `${nivel.cor}40` }}
                    >
                      <div 
                        className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg"
                        style={{ backgroundColor: `${nivel.cor}20` }}
                      >
                        <span className="text-4xl">{nivel.icone}</span>
                      </div>
                      <h4 
                        className="text-center font-bold text-lg mb-3"
                        style={{ color: nivel.cor }}
                      >
                        {nivel.nivel}
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">XP Necessário:</span>
                          <span className="font-semibold text-gray-900">
                            {nivel.xpNecessario.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Próximo Nível:</span>
                          <span className="font-semibold text-gray-900">
                            {nivel.xpProximo === 999999 ? '∞' : nivel.xpProximo.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Missões */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <i className="ri-task-line text-green-600"></i>
                    Missões ({totalMissoes})
                  </h4>
                  <button
                    onClick={adicionarMissao}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 text-sm whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                    Adicionar Missão
                  </button>
                </div>

                <div className="space-y-3">
                  {campanha.progressao.missoes.map((missao) => {
                    const isExistente = isElementoExistente(missao.id);
                    return (
                      <div key={missao.id} className={`border-2 rounded-lg p-4 hover:shadow-md transition-shadow ${
                        isExistente ? 'border-blue-200 bg-blue-50/30' : 'border-green-200 bg-green-50/30'
                      }`}>
                        {editandoMissao === missao.id ? (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-gray-600 block mb-1">Título</label>
                                <input
                                  type="text"
                                  value={missao.titulo}
                                  onChange={(e) => atualizarMissao(missao.id, 'titulo', e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-gray-600 block mb-1">Tipo</label>
                                <select
                                  value={missao.tipo}
                                  onChange={(e) => atualizarMissao(missao.id, 'tipo', e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                >
                                  <option value="diaria">Diária</option>
                                  <option value="semanal">Semanal</option>
                                  <option value="mensal">Mensal</option>
                                </select>
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-gray-600 block mb-1">Descrição</label>
                              <input
                                type="text"
                                value={missao.descricao}
                                onChange={(e) => atualizarMissao(missao.id, 'descricao', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-gray-600 block mb-1">Recompensa (XP)</label>
                                <input
                                  type="number"
                                  value={missao.recompensa}
                                  onChange={(e) => atualizarMissao(missao.id, 'recompensa', Number(e.target.value))}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-gray-600 block mb-1">Nível</label>
                                <select
                                  value={missao.nivel}
                                  onChange={(e) => atualizarMissao(missao.id, 'nivel', e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                >
                                  <option value="Bronze">Bronze</option>
                                  <option value="Prata">Prata</option>
                                  <option value="Ouro">Ouro</option>
                                  <option value="Platina">Platina</option>
                                  <option value="Diamante">Diamante</option>
                                </select>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditandoMissao(null)}
                                className="flex-1 px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm whitespace-nowrap"
                              >
                                Salvar
                              </button>
                              <button
                                onClick={() => removerMissao(missao.id)}
                                className="px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 text-sm whitespace-nowrap"
                              >
                                Remover
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <BadgeOrigem isExistente={isExistente} />
                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                  missao.tipo === 'diaria' ? 'bg-green-100 text-green-800' :
                                  missao.tipo === 'semanal' ? 'bg-blue-100 text-blue-800' :
                                  'bg-purple-100 text-purple-800'
                                }`}>
                                  {missao.tipo === 'diaria' ? 'Diária' : missao.tipo === 'semanal' ? 'Semanal' : 'Mensal'}
                                </span>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  missao.nivel === 'Bronze' ? 'bg-amber-100 text-amber-800' :
                                  missao.nivel === 'Prata' ? 'bg-gray-100 text-gray-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {missao.nivel}
                                </span>
                              </div>
                              <h5 className="text-base font-semibold text-gray-900 mb-1">{missao.titulo}</h5>
                              <p className="text-sm text-gray-600 mb-2">{missao.descricao}</p>
                              <div className="flex items-center gap-1">
                                <i className="ri-star-line text-purple-600"></i>
                                <span className="text-sm font-semibold text-purple-600">+{missao.recompensa} XP</span>
                              </div>
                            </div>
                            <button
                              onClick={() => setEditandoMissao(missao.id)}
                              className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                            >
                              <i className="ri-edit-line text-lg"></i>
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
